import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { X, CheckCircle2, XCircle, AlertTriangle, Info, Loader2 } from 'lucide-react';
import { Button } from './Button';

// ===== ANIMATIONS =====
const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

// ===== TYPES =====
export type ToastVariant = 'success' | 'error' | 'warning' | 'info' | 'loading' | 'default';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  title: string;
  description?: string;
  variant: ToastVariant;
  duration?: number;
  action?: ToastAction;
  icon?: React.ReactNode;
}

export interface ToastContextType {
  toast: (options: Omit<Toast, 'id'>) => string;
  success: (title: string, options?: Partial<Omit<Toast, 'id' | 'title' | 'variant'>> & { id?: string }) => string;
  error: (title: string, options?: Partial<Omit<Toast, 'id' | 'title' | 'variant'>> & { id?: string }) => string;
  warning: (title: string, options?: Partial<Omit<Toast, 'id' | 'title' | 'variant'>> & { id?: string }) => string;
  info: (title: string, options?: Partial<Omit<Toast, 'id' | 'title' | 'variant'>> & { id?: string }) => string;
  loading: (title: string, options?: Partial<Omit<Toast, 'id' | 'title' | 'variant'>> & { id?: string }) => string;
  dismiss: (id: string) => void;
  update: (id: string, options: Partial<Omit<Toast, 'id'>>) => void;
}

// ===== STYLED COMPONENTS =====
const ToasterContainer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  max-width: 420px;
  width: 100%;
  pointer-events: none;
  
  @media (max-width: 640px) {
    padding: 12px;
    max-width: 100%;
  }
`;

const ToastWrapper = styled.div<{ $isExiting: boolean }>`
  pointer-events: auto;
  animation: ${props => props.$isExiting ? slideOut : slideIn} 0.3s ease-out;
`;

const ToastCard = styled.div<{ $variant: ToastVariant }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: ${props => props.theme.card};
  border: 1px solid ${props => {
    switch (props.$variant) {
      case 'success': return props.theme.success + '40';
      case 'error': return props.theme.destructive + '40';
      case 'warning': return props.theme.warning + '40';
      case 'info': return props.theme.primary + '40';
      default: return props.theme.border;
    }
  }};
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 300px;
  max-width: 100%;
  
  @media (max-width: 640px) {
    min-width: auto;
    width: 100%;
  }
`;

const ToastIcon = styled.div<{ $variant: ToastVariant }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  color: ${props => {
    switch (props.$variant) {
      case 'success': return props.theme.success;
      case 'error': return props.theme.destructive;
      case 'warning': return props.theme.warning;
      case 'info': return props.theme.primary;
      case 'loading': return props.theme.primary;
      default: return props.theme.foreground;
    }
  }};
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ToastContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ToastTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.foreground};
  margin-bottom: 4px;
  line-height: 1.4;
`;

const ToastDescription = styled.div`
  font-size: 13px;
  color: ${props => props.theme.mutedForeground};
  line-height: 1.5;
  margin-bottom: ${props => props.children ? '8px' : '0'};
`;

const ToastActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const ToastActionButton = styled(Button)`
  padding: 4px 12px;
  height: auto;
  font-size: 12px;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  border: none;
  background: transparent;
  border-radius: 6px;
  color: ${props => props.theme.mutedForeground};
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
  
  &:hover {
    background: ${props => props.theme.muted};
    color: ${props => props.theme.foreground};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const SpinningLoader = styled(Loader2)`
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

// ===== CONTEXT =====
const ToastContext = createContext<ToastContextType | undefined>(undefined);

// ===== PROVIDER =====
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timeoutRefs = React.useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Cleanup timeouts on unmount
  React.useEffect(() => {
    return () => {
      timeoutRefs.current.forEach(timeout => clearTimeout(timeout));
      timeoutRefs.current.clear();
    };
  }, []);

  const dismissToast = useCallback((id: string) => {
    // Clear any existing timeout for this toast
    const timeout = timeoutRefs.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutRefs.current.delete(id);
    }
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback((options: Omit<Toast, 'id'>): string => {
    const id = Math.random().toString(36).substring(2, 9);
    const toast: Toast = {
      id,
      duration: 5000,
      ...options,
    };
    
    setToasts(prev => [...prev, toast]);
    
    // Auto-dismiss after duration
    if (toast.duration && toast.duration > 0 && toast.variant !== 'loading') {
      const timeout = setTimeout(() => {
        dismissToast(id);
      }, toast.duration);
      timeoutRefs.current.set(id, timeout);
    }
    
    return id;
  }, [dismissToast]);

  const updateToast = useCallback((id: string, options: Partial<Omit<Toast, 'id'>>) => {
    setToasts(prev => {
      const updated = prev.map(t => {
        if (t.id === id) {
          // Determine duration based on variant if not explicitly provided
          let duration = options.duration;
          if (duration === undefined) {
            // If updating from loading (duration: 0) to another variant, set appropriate duration
            if (t.variant === 'loading' && options.variant && options.variant !== 'loading') {
              duration = options.variant === 'error' ? 7000 : 5000;
            } else if (options.variant === 'error') {
              duration = 7000;
            } else if (options.variant && options.variant !== 'loading') {
              duration = 5000;
            } else {
              duration = t.duration ?? 5000;
            }
          }
          
          const updatedToast = { ...t, ...options, duration };
          
          // Clear existing timeout if any
          const existingTimeout = timeoutRefs.current.get(id);
          if (existingTimeout) {
            clearTimeout(existingTimeout);
            timeoutRefs.current.delete(id);
          }
          
          // Set up auto-dismiss if variant is not loading and duration > 0
          if (duration > 0 && updatedToast.variant !== 'loading') {
            const timeout = setTimeout(() => {
              dismissToast(id);
            }, duration);
            timeoutRefs.current.set(id, timeout);
          }
          
          return updatedToast;
        }
        return t;
      });
      return updated;
    });
  }, [dismissToast]);

  const toast = useCallback((options: Omit<Toast, 'id'>) => {
    return addToast({ ...options, variant: options.variant || 'default' });
  }, [addToast]);

  const success = useCallback((title: string, options?: Partial<Omit<Toast, 'id' | 'title' | 'variant'>> & { id?: string }) => {
    const { id, ...restOptions } = options || {};
    if (id) {
      // Update existing toast
      updateToast(id, {
        title,
        variant: 'success',
        icon: <CheckCircle2 />,
        duration: restOptions.duration ?? 5000,
        ...restOptions,
      });
      return id;
    }
    return addToast({
      title,
      variant: 'success',
      icon: <CheckCircle2 />,
      duration: restOptions?.duration ?? 5000,
      ...restOptions,
    });
  }, [addToast, updateToast]);

  const error = useCallback((title: string, options?: Partial<Omit<Toast, 'id' | 'title' | 'variant'>> & { id?: string }) => {
    const { id, ...restOptions } = options || {};
    if (id) {
      // Update existing toast
      updateToast(id, {
        title,
        variant: 'error',
        icon: <XCircle />,
        duration: 7000,
        ...restOptions,
      });
      return id;
    }
    return addToast({
      title,
      variant: 'error',
      icon: <XCircle />,
      duration: 7000, // Longer duration for errors
      ...restOptions,
    });
  }, [addToast, updateToast]);

  const warning = useCallback((title: string, options?: Partial<Omit<Toast, 'id' | 'title' | 'variant'>> & { id?: string }) => {
    const { id, ...restOptions } = options || {};
    if (id) {
      updateToast(id, {
        title,
        variant: 'warning',
        icon: <AlertTriangle />,
        duration: restOptions.duration ?? 5000,
        ...restOptions,
      });
      return id;
    }
    return addToast({
      title,
      variant: 'warning',
      icon: <AlertTriangle />,
      duration: restOptions?.duration ?? 5000,
      ...restOptions,
    });
  }, [addToast, updateToast]);

  const info = useCallback((title: string, options?: Partial<Omit<Toast, 'id' | 'title' | 'variant'>> & { id?: string }) => {
    const { id, ...restOptions } = options || {};
    if (id) {
      updateToast(id, {
        title,
        variant: 'info',
        icon: <Info />,
        duration: restOptions.duration ?? 5000,
        ...restOptions,
      });
      return id;
    }
    return addToast({
      title,
      variant: 'info',
      icon: <Info />,
      duration: restOptions?.duration ?? 5000,
      ...restOptions,
    });
  }, [addToast, updateToast]);

  const loading = useCallback((title: string, options?: Partial<Omit<Toast, 'id' | 'title' | 'variant'>> & { id?: string }) => {
    const { id, ...restOptions } = options || {};
    if (id) {
      updateToast(id, {
        title,
        variant: 'loading',
        icon: <SpinningLoader />,
        duration: 0,
        ...restOptions,
      });
      return id;
    }
    return addToast({
      title,
      variant: 'loading',
      icon: <SpinningLoader />,
      duration: 0, // Loading toasts don't auto-dismiss
      ...restOptions,
    });
  }, [addToast, updateToast]);

  const value: ToastContextType = {
    toast,
    success,
    error,
    warning,
    info,
    loading,
    dismiss: dismissToast,
    update: updateToast,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toaster toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
};

// ===== HOOK =====
export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// ===== TOASTER COMPONENT =====
interface ToasterProps {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}

const Toaster: React.FC<ToasterProps> = ({ toasts, onDismiss }) => {
  const [exiting, setExiting] = useState<Set<string>>(new Set());

  const handleDismiss = (id: string) => {
    setExiting(prev => new Set(prev).add(id));
    setTimeout(() => {
      onDismiss(id);
      setExiting(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 300);
  };

  if (toasts.length === 0) return null;

  return createPortal(
    <ToasterContainer>
      {toasts.map(toast => (
        <ToastWrapper key={toast.id} $isExiting={exiting.has(toast.id)}>
          <ToastCard $variant={toast.variant}>
            {toast.icon && (
              <ToastIcon $variant={toast.variant}>
                {toast.icon}
              </ToastIcon>
            )}
            <ToastContent>
              <ToastTitle>{toast.title}</ToastTitle>
              {toast.description && (
                <ToastDescription>{toast.description}</ToastDescription>
              )}
              {toast.action && (
                <ToastActions>
                  <ToastActionButton
                    variant="outline"
                    size="sm"
                    onClick={toast.action.onClick}
                  >
                    {toast.action.label}
                  </ToastActionButton>
                </ToastActions>
              )}
            </ToastContent>
            {toast.variant !== 'loading' && (
              <CloseButton onClick={() => handleDismiss(toast.id)}>
                <X />
              </CloseButton>
            )}
          </ToastCard>
        </ToastWrapper>
      ))}
    </ToasterContainer>,
    document.body
  );
};

